import React, { useState, useEffect, useMemo, useCallback } from "react";
import Slider from "../../components/sliders/Slider";
import "./styles.css";
import { useNavigate } from "react-router-dom";
import { AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import Tooltip from "@mui/material/Tooltip";
import More from "../../assets/images/MoreVert.png";
import defaultImage from "../../assets/images/default-not-found-image-removebg-preview.png";
import { formatUSAPrice } from "../../helpers/utils";
import getTimeAgo from "../../pages/utils";

function PropertyComponent(props) {
    const {
        item,
        isProperties,
        isMyProperties,
        setEditModal,
        handleFavorite,
        onMouseEnter,
        properties,
        onMouseLeave,
        isModalOpen,
        isHomePage,
        setPreviewModal,
        isFavorite: isFav,
        isUnfavoriteLoading = false,
    } = props;

    const navigate = useNavigate();
    const isFavorite = isFav !== undefined ? isFav : item?.isFavorite;
    const [initialImage, setInitialImage] = useState(null); // First image
    const [remainingImages, setRemainingImages] = useState([]); // Remaining images
    const [imageLoadErrors, setImageLoadErrors] = useState(new Set()); // Track failed images
    const [validImages, setValidImages] = useState([]);
    const handleClick = () => {
        if (isHomePage) {
            return setPreviewModal(item);
        }
        if (isMyProperties) {
            setEditModal(item);
        } else {
            navigate(`/preview/${item.ListingId || item.property_id}`, {
                state: { properties },
            });
        }
    };

    const images = useMemo(() => {
        if (!item.images || !Array.isArray(item.images)) {
            return [];
        }
        if (!isMyProperties) {
            return item.images
                .filter((media) => media.MediaKey && typeof media.MediaKey === "string")
                .slice(0, 10) // Limit to first 10 images for performance
                .map((media) => "https://image.onmls.com/public/images/" + media.MediaKey);
        }
        return item.images
            .filter((media) => media.image_url && typeof media.image_url === "string")
            .slice(0, 10) // Limit to first 10 images for performance
            .map((media) => media.image_url);
    }, [item.images, isMyProperties]);

    useEffect(() => {
        if (images.length > 0) {
            setInitialImage(images[0]); // Set the first image immediately
            // Lazy load remaining images after a short delay
            const timer = setTimeout(() => {
                setRemainingImages(images.slice(1));
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [images]);

    useEffect(() => {
        const imgData = [initialImage, ...remainingImages].filter((url) => url && !imageLoadErrors.has(url));
        setValidImages(imgData);
    }, [remainingImages, initialImage, imageLoadErrors]);

    const handleImageError = useCallback((url, err) => {
        setImageLoadErrors((prevErrors) => new Set([...prevErrors, url]));
    }, []);

    function toTitleCase(address) {
        return address.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    }

    const handleManagePlanClick = async (e) => {
        e.stopPropagation();
        navigate(`/MyProperties/${item.property_id}/manage-plan`);
    };

    return (
        <div
            className={`tw-property-wrapper tw-shadow-md hover:tw-shadow-lg hover:tw-shadow-neutral-400 tw-bg-white tw-cursor-pointer  tw-rounded-[5px]   ${isProperties ? "custom-property-item" : ""} tw-w-full`}
            onClick={handleClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <div className="tw-relative tw-w-full tw-h-fit ">
                {item.status && isMyProperties && (
                    <p
                        className={`
                            ${item.status === "rejected" ? "tw-bg-red-500" : item.status === "approved" ? "tw-bg-green-500" : "tw-bg-yellow-500"}
                            tw-absolute tw-top-3 tw-left-3 tw-text-black tw-px-2 tw-py-1 tw-rounded-md tw-text-[12px] tw-z-[2]`}
                    >
                        {item.status === "rejected" ? "Rejected" : item.status === "approved" ? "Active" : "Pending"}
                    </p>
                )}
                {validImages && validImages.length > 0 ? (
                    <div className="tw-relative tw-max-h-[170px] tw-overflow-hidden tw-h-full">
                        {/* Render Slider without pre-loading all images */}
                        <Slider
                            images={validImages} // Pass all valid images to the slider
                            onImageError={(index, url) => handleImageError(url, index)}
                        />

                        {item.ListingContractDate && (
                            <p className="tw-bg-background_color tw-absolute tw-top-3 tw-left-3 tw-text-black tw-px-2 tw-py-1 tw-rounded-md tw-text-[12px]">
                                {getTimeAgo(item.ListingContractDate)}
                            </p>
                        )}

                        <span className="tw-absolute tw-top-3 tw-right-3">
                            {!isMyProperties && !isModalOpen && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isUnfavoriteLoading) {
                                            handleFavorite(item);
                                        }
                                    }}
                                    disabled={isUnfavoriteLoading}
                                    className={`${isUnfavoriteLoading ? "tw-opacity-50 tw-cursor-not-allowed" : ""}`}
                                >
                                    {isFavorite ? (
                                        <AiFillHeart
                                            className={`tw-text-secondary tw-text-[24px] ${isUnfavoriteLoading ? "tw-animate-pulse" : ""}`}
                                        />
                                    ) : (
                                        <AiOutlineHeart className="tw-text-[24px] tw-text-white" color="black" />
                                    )}
                                </button>
                            )}
                        </span>
                    </div>
                ) : (
                    <div className="tw-w-full tw-rounded-[6px] 2xl:tw-text-[25px] tw-flex tw-pt-5 tw-items-center tw-justify-center tw-bg-gray-300 tw-text-black tw-h-[170px]">
                        {/* No Images added yet! */}
                        <img src={defaultImage} alt={`Image Not Found`} className="" />
                    </div>
                )}
                <div className=" tw-left-0 tw-w-full tw-p-2 tw-flex tw-flex-col tw-gap-[5px] tw-z-20 tw-rounded-b-xl">
                    <div className="tw-flex tw-w-full tw-justify-between  tw-text-[14px] 2xl:tw-text-[18px]">
                        <div className="tw-flex tw-items-center tw-justify-between">
                            <p className="PriceTextCss tw-text-[18px] tw-tracking-wide">${formatUSAPrice(item?.price) || "Price $0"} </p>
                        </div>
                        {isMyProperties && (
                            <button onClick={(e) => handleManagePlanClick(e)} className="tw-text-[9px] tw-text-primary tw-underline tw-font-[700]">
                                Manage Plan
                            </button>
                        )}
                    </div>
                    <div className="tw-flex tw-items-center  tw-gap-[5px] tw-text-[11px] 2xl:tw-text-[16px] tw-text-black">
                        <div className="tw-flex tw-items-center tw-font-semibold tw-gap-[5px] ">
                            {/* <BedOutlined fontSize="20px" className="tw-text-secondary tw-text-[18px]" /> */}
                            <p className="tw-text-[12px]">{item.beds || 0} Beds</p>
                        </div>
                        |
                        <div className="tw-flex tw-items-center tw-font-semibold tw-gap-[5px]">
                            {/* <BathtubOutlinedIcon fontSize="20px" className="tw-text-secondary tw-text-[18px]" /> */}
                            <p className="tw-text-[12px]">{item.baths || 0} Baths</p>
                        </div>
                        |
                        <div className="tw-flex tw-items-center tw-font-semibold tw-gap-[5px]">
                            {/* <FaChartArea className="tw-text-secondary tw-text-[18px]" /> */}
                            <p className="tw-text-[12px]">{item.LivingArea ? `${item.LivingArea} sqft` : "0 sqft"}</p>
                        </div>
                    </div>
                    {/* <hr color="#D0D5DD" className="tw-mt-4" /> */}
                    <div className="">
                        <div className="tw-flex  tw-justify-between tw-font-semibold tw-items-center tw-gap-2 tw-min-h-[50px]">
                            <Tooltip title={""} placement="top" arrow>
                                {/* <p className="addressTextCss">{item.address || "No Address Provided"}</p> */}
                                {item.address ? (
                                    <p className=" tw-text-[13px]">{item.address}</p>
                                ) : (
                                    <p className=" tw-text-[13px]">
                                        {toTitleCase(
                                            [
                                                item.UnitNumber,
                                                item.StreetNumber,
                                                item.StreetName,
                                                item.StreetSuffix,
                                                item.City,
                                                item.StateOrProvince,
                                                item.PostalCode,
                                            ]
                                                .filter(Boolean) // removes undefined, null, or empty strings
                                                .join(" ")
                                        )}
                                    </p>
                                )}
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PropertyComponent;
